/**
 * Support Contact Utilities
 * 
 * Centralized utilities for generating support contact emails with rich error context.
 * Provides automatic error context gathering, console log capture, and privacy filtering.
 */

// Support email configuration
export const SUPPORT_EMAIL = 'support@bridgefirstforecasting.com';

// Console log buffer (circular buffer with fixed size)
const LOG_BUFFER_SIZE = 20;
const logBuffer = new Array(LOG_BUFFER_SIZE);
let logIndex = 0;
let consoleInterceptInitialized = false;

// Sensitive data patterns to filter
const SENSITIVE_PATTERNS = [
  { pattern: /password[:\s]*[^\s,]*/gi, replacement: 'password: [REDACTED]' },
  { pattern: /token[:\s]*[^\s,]*/gi, replacement: 'token: [REDACTED]' },
  { pattern: /secret[:\s]*[^\s,]*/gi, replacement: 'secret: [REDACTED]' },
  { pattern: /key[:\s]*[^\s,]*/gi, replacement: 'key: [REDACTED]' },
  { pattern: /auth[:\s]*[^\s,]*/gi, replacement: 'auth: [REDACTED]' },
  { pattern: /bearer[:\s]*[^\s,]*/gi, replacement: 'bearer: [REDACTED]' },
  { pattern: /jwt[:\s]*[^\s,]*/gi, replacement: 'jwt: [REDACTED]' },
  { pattern: /api[_-]?key[:\s]*[^\s,]*/gi, replacement: 'api_key: [REDACTED]' },
];

/**
 * Filters sensitive data from a string
 * @param {string} text - Text to filter
 * @returns {string} Filtered text with sensitive data redacted
 */
export function filterSensitiveData(text) {
  if (!text || typeof text !== 'string') return text;
  
  let filtered = text;
  SENSITIVE_PATTERNS.forEach(({ pattern, replacement }) => {
    filtered = filtered.replace(pattern, replacement);
  });
  
  return filtered;
}

/**
 * Captures a log entry in the circular buffer
 * @param {Array} args - Console arguments
 */
function captureLog(...args) {
  try {
    const message = args.map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg);
        } catch (e) {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');
    
    // Filter sensitive data before storing
    const filteredMessage = filterSensitiveData(message);
    
    // Store in circular buffer
    logBuffer[logIndex] = {
      timestamp: Date.now(),
      message: filteredMessage.substring(0, 500), // Limit message length
    };
    
    logIndex = (logIndex + 1) % LOG_BUFFER_SIZE;
  } catch (error) {
    // Don't let logging errors break the app
    console.warn('[SupportContact] Error capturing log:', error);
  }
}

/**
 * Sets up console.error interception to capture logs
 * Should be called once during app initialization
 */
export function setupConsoleCapture() {
  if (consoleInterceptInitialized) {
    return; // Already initialized
  }
  
  try {
    const originalError = console.error;
    
    console.error = function(...args) {
      // Capture the log
      captureLog(...args);
      
      // Call original console.error
      originalError.apply(console, args);
    };
    
    consoleInterceptInitialized = true;
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[SupportContact] Console capture initialized');
    }
  } catch (error) {
    console.warn('[SupportContact] Failed to initialize console capture:', error);
  }
}

/**
 * Gets recent console logs from the buffer
 * @param {number} count - Number of logs to retrieve (default: 10)
 * @returns {Array<{timestamp: number, message: string}>} Recent logs
 */
export function getRecentConsoleLogs(count = 10) {
  try {
    // Get logs in chronological order
    const logs = [];
    const actualCount = Math.min(count, LOG_BUFFER_SIZE);
    
    for (let i = 0; i < actualCount; i++) {
      const index = (logIndex - actualCount + i + LOG_BUFFER_SIZE) % LOG_BUFFER_SIZE;
      const log = logBuffer[index];
      
      if (log && log.message) {
        logs.push(log);
      }
    }
    
    return logs;
  } catch (error) {
    console.warn('[SupportContact] Error retrieving logs:', error);
    return [];
  }
}

/**
 * Formats a timestamp for display
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {string} Formatted timestamp
 */
function formatTimestamp(timestamp) {
  try {
    const date = new Date(timestamp);
    return date.toISOString();
  } catch (error) {
    return String(timestamp);
  }
}

/**
 * Formats error details for email body
 * @param {Object} params - Parameters
 * @param {string} params.errorMessage - User-facing error message
 * @param {Object} params.errorDetails - Technical error details
 * @param {Object} params.context - Additional context
 * @param {Object} params.userInfo - User information
 * @param {Array} params.recentLogs - Recent console logs
 * @returns {string} Formatted email body
 */
export function formatErrorForEmail({
  errorMessage = '',
  errorDetails = {},
  context = {},
  userInfo = {},
  recentLogs = []
}) {
  const sections = [];
  
  // Header
  sections.push('ERROR REPORT');
  sections.push('============\n');
  
  // Error Message
  if (errorMessage) {
    sections.push('Error Message:');
    sections.push(errorMessage);
    sections.push('');
  }
  
  // User Information
  if (userInfo.email || userInfo.organization || userInfo.userId) {
    sections.push('User Information:');
    if (userInfo.email) sections.push(`- Email: ${userInfo.email}`);
    if (userInfo.organization) sections.push(`- Organization: ${userInfo.organization}`);
    if (userInfo.userId) sections.push(`- User ID: ${userInfo.userId}`);
    if (userInfo.role) sections.push(`- Role: ${userInfo.role}`);
    sections.push('');
  }
  
  // Context
  const contextEntries = Object.entries(context).filter(([_, value]) => value != null);
  if (contextEntries.length > 0) {
    sections.push('Context:');
    contextEntries.forEach(([key, value]) => {
      sections.push(`- ${key}: ${value}`);
    });
    sections.push('');
  }
  
  // Page Information
  sections.push('Page Information:');
  sections.push(`- URL: ${window.location.href}`);
  sections.push(`- Timestamp: ${formatTimestamp(Date.now())}`);
  sections.push('');
  
  // Browser Information
  sections.push('Browser Information:');
  sections.push(`- User Agent: ${navigator.userAgent}`);
  sections.push(`- Platform: ${navigator.platform}`);
  if (navigator.language) sections.push(`- Language: ${navigator.language}`);
  sections.push('');
  
  // Technical Details
  if (errorDetails.stack || errorDetails.statusCode || errorDetails.endpoint) {
    sections.push('Technical Details:');
    if (errorDetails.statusCode) sections.push(`- Status Code: ${errorDetails.statusCode}`);
    if (errorDetails.endpoint) sections.push(`- Endpoint: ${errorDetails.endpoint}`);
    if (errorDetails.isTimeout) sections.push(`- Is Timeout: ${errorDetails.isTimeout}`);
    if (errorDetails.stack) {
      sections.push('- Stack Trace:');
      // Limit stack trace length
      const stack = String(errorDetails.stack).substring(0, 1000);
      sections.push(stack);
    }
    sections.push('');
  }
  
  // Recent Console Logs
  if (recentLogs.length > 0) {
    sections.push('Recent Console Logs:');
    recentLogs.forEach(log => {
      const timestamp = formatTimestamp(log.timestamp);
      sections.push(`[${timestamp}] ${log.message}`);
    });
    sections.push('');
  }
  
  return sections.join('\n');
}

/**
 * Generates a mailto URL with pre-populated subject and body
 * @param {Object} params - Parameters
 * @param {string} params.subject - Email subject
 * @param {string} params.errorMessage - User-facing error message
 * @param {Object} params.errorDetails - Technical error details
 * @param {Object} params.context - Additional context
 * @returns {string} Mailto URL
 */
export function generateSupportMailto({
  subject = 'Application Error',
  errorMessage = '',
  errorDetails = {},
  context = {}
}) {
  try {
    // Gather user information from context or global state
    const userInfo = {
      email: context.userEmail || '',
      organization: context.organizationName || '',
      userId: context.userId || '',
      role: context.userRole || ''
    };
    
    // Get recent console logs
    const recentLogs = getRecentConsoleLogs(10);
    
    // Format email body
    let emailBody = formatErrorForEmail({
      errorMessage,
      errorDetails,
      context,
      userInfo,
      recentLogs
    });
    
    // Check URL length and truncate if necessary
    // Mailto URLs have a practical limit of ~2000 characters
    const maxBodyLength = 1500; // Leave room for subject and email address
    if (emailBody.length > maxBodyLength) {
      emailBody = emailBody.substring(0, maxBodyLength) + '\n\n[Content truncated due to length]';
    }
    
    // Encode for URL
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(emailBody);
    
    return `mailto:${SUPPORT_EMAIL}?subject=${encodedSubject}&body=${encodedBody}`;
  } catch (error) {
    console.error('[SupportContact] Error generating mailto:', error);
    // Fallback to simple mailto
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  }
}

/**
 * Extracts user context from auth context or active user
 * @param {Object} activeUser - Active user object
 * @returns {Object} User context for email
 */
export function extractUserContext(activeUser) {
  if (!activeUser) return {};
  
  return {
    userEmail: activeUser.email || '',
    organizationName: activeUser.organization?.name || '',
    userId: activeUser.id || '',
    userRole: activeUser.user_type || ''
  };
}
